import {
  formatPremiumAmount,
  premiumCurrency,
} from '../../../calculatedAttributes/providers/tiQuote';
import { CalculatedAttributeResult } from '../../../calculatedAttributes/resolve';
import { useCmsClientContext } from '../../../utils/cmsPageContext';

interface PriceCellProps {
  /**
   * The engine's answer for this row: loader, price, or dash. Any provider is
   * accepted; only a travel quote is a price — anything else renders the dash.
   */
  result: CalculatedAttributeResult | undefined;
}

/**
 * Contents of the calculated `TI_PREMIUM_PRICE` cell in a Quick Answer table
 * (CMS-1956 R5). The `<td>` and its padding belong to the table, so this
 * renders the value only — unlike Table Builder's price cell, which owns its
 * own cell box.
 *
 * A pure formatter: prices are resolved upstream and never authored. While the
 * quote poll is still running an unquoted cell shows a skeleton; once settled,
 * a missing quote renders "-" and leaves the row visible.
 *
 * Deliberately without the discount badge Table Builder carries: Quick Answer
 * cells are narrower and the badge does not fit (R5).
 */
export const PriceCell = ({ result }: PriceCellProps) => {
  const { currencyDisplay } = useCmsClientContext();
  const quote = result?.provider === 'TI_QUOTE' ? result : undefined;
  const price = quote?.state === 'resolved' ? quote.value : undefined;
  const currency = premiumCurrency(price, currencyDisplay);

  if (!price) {
    return quote?.state === 'pending' ? (
      // Same skeleton idiom as the Table Builder price cell: bars sized to the
      // settled layout, so filling them in does not shift the row height.
      <div className="flex flex-col gap-spacing/gap/soft-6px">
        <span className="h-5 w-16 animate-pulse rounded-radius/button bg-fill/alternative-3" />
        <span className="h-3 w-12 animate-pulse rounded-radius/button bg-fill/alternative-3" />
      </div>
    ) : (
      <span className="font-font-weight/table-cell-normal text-font-size/table-cell leading-line-height/table-cell text-text/primary">
        -
      </span>
    );
  }

  // Both halves or neither: a struck-through price equal to the one above it
  // reads as a rendering bug rather than as a discount.
  const hasDiscount =
    price.originalAmount !== undefined && price.originalAmount > price.amount;

  return (
    <div className="flex flex-col gap-spacing/gap/soft-4px">
      <div className="flex items-baseline gap-0.5 whitespace-nowrap">
        <span className="font-font-weight/caption text-font-size/table-cell leading-line-height/table-cell text-text/secondary-lighter">
          {currency}
        </span>
        <span className="font-font-weight/table-cell-primary text-font-size/table-cell-primary leading-line-height/table-cell-primary text-text/primary">
          {formatPremiumAmount(price.amount)}
        </span>
      </div>
      {hasDiscount && (
        <span className="font-font-weight/table-cell-normal text-font-size/table-cell leading-line-height/table-cell text-text/secondary-lighter line-through whitespace-nowrap">
          {currency}
          {formatPremiumAmount(price.originalAmount!)}
        </span>
      )}
    </div>
  );
};
